import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatRelative } from "../lib/api";
import AppShell from "../components/AppShell";
import AuthorAvatar from "../components/AuthorAvatar";
import { Microphone, Clock, Play, Sparkle, MicrophoneStage, ArrowRight } from "@phosphor-icons/react";

const TYPE_META = {
  interview: { label: "INTERVIEW", icon: MicrophoneStage },
  story:     { label: "USER STORY", icon: Play },
  deepdive:  { label: "DEEP DIVE", icon: Sparkle },
};

function formatDuration(sec) {
  if (!sec) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function Podcasts() {
  const [episodes, setEpisodes] = useState(null);
  const [pillars, setPillars] = useState([]);
  const [activePillar, setActivePillar] = useState("all");
  const [activeType, setActiveType] = useState("all");

  useEffect(() => {
    api.get("/podcasts/pillars").then((r) => setPillars(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (activePillar !== "all") params.pillar = activePillar;
    if (activeType !== "all") params.episode_type = activeType;
    api.get("/podcasts", { params }).then((r) => setEpisodes(r.data)).catch(() => setEpisodes([]));
  }, [activePillar, activeType]);

  const pillarMap = useMemo(() => Object.fromEntries((pillars || []).map((p) => [p.key, p])), [pillars]);

  return (
    <AppShell>
      <div data-testid="podcasts-page">
        <div className="mb-10">
          <div className="label-mono mb-2 flex items-center gap-2" style={{ color: "#E60012" }}>
            <Microphone size={12} weight="fill" /> PODCAST · MOBILITY INTELLIGENCE ON-AIR
          </div>
          <h1 className="font-display text-white mb-3" style={{ fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 0.98 }}>
            The voices<br /><span style={{ color: "#E60012" }}>moving mobility.</span>
          </h1>
          <p className="text-zinc-400 max-w-2xl">
            Expert interviews, user stories, and technical deep-dives across four pillars —
            aerospace integration, EV intelligence, urban mobility systems, and the regulatory horizon.
            Produced by PUNCH AI.
          </p>
        </div>

        {/* Pillar chips */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="podcast-pillar-chips">
          <button
            onClick={() => setActivePillar("all")}
            className={`label-mono px-3 py-1.5 border ${activePillar === "all" ? "border-white text-white bg-[#1A1A1D]" : "border-[#27272A] text-zinc-400 hover:text-white"}`}
            data-testid="podcast-pillar-all"
          >ALL PILLARS</button>
          {pillars.map((p) => (
            <button
              key={p.key}
              onClick={() => setActivePillar(p.key)}
              className={`label-mono px-3 py-1.5 border flex items-center gap-1.5 ${activePillar === p.key ? "border-white text-white bg-[#1A1A1D]" : "border-[#27272A] text-zinc-400 hover:text-white"}`}
              style={activePillar === p.key ? { borderColor: p.color, color: p.color } : {}}
              data-testid={`podcast-pillar-${p.key}`}
            >
              <span className="hub-dot" style={{ color: p.color }} />
              {p.name.toUpperCase()}
            </button>
          ))}
        </div>
        {/* Format chips */}
        <div className="flex flex-wrap gap-2 mb-8 label-mono" data-testid="podcast-type-chips">
          <button onClick={() => setActiveType("all")} className={`px-2.5 py-1 border ${activeType === "all" ? "border-white text-white" : "border-[#27272A] text-zinc-500 hover:text-white"}`}>ALL FORMATS</button>
          <button onClick={() => setActiveType("interview")} className={`px-2.5 py-1 border ${activeType === "interview" ? "border-white text-white" : "border-[#27272A] text-zinc-500 hover:text-white"}`}>INTERVIEWS</button>
          <button onClick={() => setActiveType("story")} className={`px-2.5 py-1 border ${activeType === "story" ? "border-white text-white" : "border-[#27272A] text-zinc-500 hover:text-white"}`}>USER STORIES</button>
          <button onClick={() => setActiveType("deepdive")} className={`px-2.5 py-1 border ${activeType === "deepdive" ? "border-white text-white" : "border-[#27272A] text-zinc-500 hover:text-white"}`}>DEEP DIVES</button>
        </div>

        {episodes === null ? (
          <div className="py-16 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING EPISODES...</div>
        ) : episodes.length === 0 ? (
          <div className="border border-dashed border-[#27272A] p-12 text-center">
            <Microphone size={32} weight="light" className="mx-auto mb-3 text-zinc-600" />
            <div className="label-mono mb-2">NO EPISODES MATCH</div>
            <p className="text-zinc-400 text-sm">Try a different pillar or format.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="podcast-grid">
            {episodes.map((ep) => {
              const pillar = pillarMap[ep.pillar] || { name: ep.pillar, color: "#E60012" };
              const Meta = TYPE_META[ep.episode_type] || TYPE_META.interview;
              const Icon = Meta.icon;
              const cover = ep.cover_image ? `${process.env.REACT_APP_BACKEND_URL}${ep.cover_image}` : "";
              const ready = !!ep.transcript_md;
              return (
                <Link
                  key={ep.id}
                  to={`/podcasts/${ep.slug}`}
                  className="card card-hover overflow-hidden flex flex-col"
                  data-testid={`podcast-card-${ep.slug}`}
                  style={{ borderTop: `2px solid ${pillar.color}` }}
                >
                  <div className="aspect-square bg-[#0A0A0C] relative overflow-hidden">
                    {cover ? (
                      <img src={cover} alt="" className="w-full h-full object-cover" loading="lazy"
                           onError={(e) => { e.target.style.display = "none"; }} />
                    ) : (
                      <div className="w-full h-full blueprint-grid-dense flex items-center justify-center">
                        <Microphone size={48} weight="light" style={{ color: pillar.color }} />
                      </div>
                    )}
                    <div className="absolute top-2 left-2 label-mono px-2 py-1 bg-[#0A0A0C]/90 border flex items-center gap-1" style={{ color: pillar.color, borderColor: pillar.color }}>
                      <span className="hub-dot mr-0.5" style={{ color: pillar.color }} />
                      {pillar.name.toUpperCase()}
                    </div>
                    <div className="absolute top-2 right-2 label-mono px-2 py-1 bg-[#0A0A0C]/90 border border-[#27272A] flex items-center gap-1 text-zinc-300">
                      <Icon size={9} weight="bold" /> {Meta.label}
                    </div>
                    {!ready && (
                      <div className="absolute bottom-2 left-2 label-mono px-2 py-1 bg-[#0A0A0C]/90 border" style={{ color: "#FFAB00", borderColor: "#FFAB00" }} data-testid={`podcast-status-generating-${ep.slug}`}>
                        <Sparkle size={9} weight="fill" className="inline mr-1" />GENERATING
                      </div>
                    )}
                    {ready && ep.audio_url && (
                      <div className="absolute bottom-3 right-3 w-12 h-12 rounded-full bg-[#E60012] flex items-center justify-center shadow-2xl">
                        <Play size={18} weight="fill" className="text-white ml-0.5" />
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-display text-white text-base mb-2 line-clamp-2" style={{ fontWeight: 700, letterSpacing: "-0.015em" }}>
                      {ep.title}
                    </h3>
                    <p className="text-sm text-zinc-400 line-clamp-3 mb-3 flex-1">{ep.summary}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-dashed border-[#27272A]">
                      <span className="label-mono flex items-center gap-1"><Clock size={10} /> {formatDuration(ep.duration_sec)}</span>
                      <span className="label-mono flex items-center gap-1">LISTEN <ArrowRight size={9} weight="bold" /></span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
